/**
 * The sheet about what stands on a tile (the panel's tile): opened by its Values button, closed by
 * × or by walking off the tile (game/view/panels.ts). The planet stays free beside it. Each role
 * shows its own values (an extractor's, a charge's, an infuser's); an extractor its level in a pill and a way
 * into its upgrades, a page of their own (ui/features/upgrades) with the back button to return;
 * Remove at the foot. All of it in the machine's own colour.
 */
import { useState, type JSX } from 'react';

import { MACHINE_COLOR } from '@core/palette';
import { factsAt } from '@game/planet/here';
import { blastMap, reachMap, ringMap } from '@game/planet/reach';
import { closePanel } from '@game/view/panels';
import { machineName, t } from '@i18n/index';
import { Sheet } from '@ui/components/Sheet';
import { SheetPages } from '@ui/components/SheetPages';
import { useGame, useGameState, useLocale, useTick } from '@ui/hooks';
import { Icon } from '@ui/icons';

import { UpgradesEntry } from '../upgrades/UpgradesEntry';
import { UpgradesPage } from '../upgrades/UpgradesPage';
import { ChargeStats } from './ChargeStats';
import { ExtractorStats } from './ExtractorStats';
import { InfuserStats } from './InfuserStats';
import { RemoveRow } from './RemoveRow';

import './info.css';

type Page = 'values' | 'upgrades';

export function InfoSheet(): JSX.Element | null {
  useLocale();
  const game = useGame();
  const tile = useGameState((s) => s.panelTile);
  useGameState((s) => s.planetVersion);
  // what a machine holds and a charge's fuse move with time, not with the store
  useTick(factsAt(game, tile)?.machine?.role === 'charge' ? 250 : 1000);
  const [page, setPage] = useState<Page>('values');
  const machine = factsAt(game, tile)?.machine;
  const view = game.store.get().view;
  if (!machine || !view) return null;
  const name = machineName(machine.type);
  const values = page === 'values';
  return (
    <Sheet
      id="info"
      title={values ? name : t('upgrade.title')}
      badge={
        values &&
        machine.role === 'extractor' && (
          <span className="level-pill">
            <Icon id="star" />
            {t('info.level', { n: machine.level })}
          </span>
        )
      }
      back={values ? undefined : () => setPage('values')}
      onClose={() => closePanel(game)}
      className="info"
      tone={MACHINE_COLOR[machine.type]?.ui}
      free
    >
      <SheetPages
        page={page}
        pages={{
          values: (
            <>
              {machine.role === 'extractor' ? (
                <ExtractorStats info={machine} reach={reachMap(view, tile)} />
              ) : machine.role === 'charge' ? (
                <ChargeStats info={machine} area={blastMap(view, tile)} tile={tile} />
              ) : (
                <InfuserStats info={machine} ring={ringMap(view, tile, machine.next)} tile={tile} />
              )}
              <UpgradesEntry tile={tile} name={name} onOpen={() => setPage('upgrades')} />
              <RemoveRow tile={tile} name={name} />
            </>
          ),
          upgrades: <UpgradesPage tile={tile} type={machine.type} name={name} />,
        }}
      />
    </Sheet>
  );
}
